import React from 'react';
import PropTypes from 'prop-types';
import uuidv1 from 'uuid/v1';

import { cssTransform } from 'utils/css-utils';
import { getConfigNotes, newConfigNoteID } from 'utils/project-definition/note-utils';

import NoteObject from './note-object';
import NoteInfoBubble from './note-info-bubble';

export default class NoteLayer extends React.Component {
  constructor(props) {
    super(props);

    // bind methods

    this.noteClickHandler = this.noteClickHandler.bind(this);
    this.notePressHandler = this.notePressHandler.bind(this);
    this.noteDraggedHandler = this.noteDraggedHandler.bind(this);
    this.updateNoteContentHandler = this.updateNoteContentHandler.bind(this);

    // init state
    this.state = {
      openedNote: null,
    };
  }

  /* Event handlers */

  updatedNoteContentHandler(note) {
    if (this.props.onUpdateInfoBubbleContent) {
      this.props.onUpdateInfoBubbleContent(note);
    }
  }

  updateNoteContentHandler(value) {
    const { openedNote } = this.state;
    if (!openedNote) {
      return;
    }
    openedNote.content = value;
    this.setState({ openedNote });
    this.updatedNoteContentHandler(openedNote);
  }

  noteDraggedHandler(note, evt) {
    const { onDraggedNote } = this.props;
    if (onDraggedNote) {
      onDraggedNote(note, this, evt);
    }
  }

  noteClickHandler(note, evt) {
    const { onClickNote } = this.props;
    if (onClickNote) {
      const result = onClickNote(note, this, evt);
      if (result === false) {
        return;
      }
    }
    this.setNoteOpened(note, !this.isNoteOpened(note));
  }

  notePressHandler(note, noteIndex, comp, evt) {
    if (!this.props.onPressNote) {
      return;
    }
    this.props.onPressNote(note, noteIndex, this, comp, evt);
  }

  noteClickCloseHandler(note) {
    this.setNoteOpened(note, false);
  }

  noteClickOptionHandler(note, option) {
    let res = null;
    if (this.props.onClickNoteOption) {
      res = this.props.onClickNoteOption(note, option, this);
      if (res === false) {
        return;
      }
    }
    if (option === 'remove') {
      this.removeNoteHandler(note);
    }
  }
  /* Methods */

  getNoteComponentInfo(note, map = null) {
    if (!note) {
      return null;
    }
    const m = map || this.getNoteComponentInfoMap();
    if (m) {
      let id;
      if (typeof (note) === 'string') {
        id = note;
      } else {
        id = note.id;
      }
      if (typeof (id) === 'number') {
        id = id.toString(16);
      }
      if (id) {
        return m[id];
      }
    }
    return null;
  }

  setNoteComponentInfo(note, info, map) {
    if (!note) {
      return;
    }
    const m = map || this.getNoteComponentInfoMap();
    const n = note;
    let id = n.id;
    if (typeof (id) === 'number') {
      id = id.toString(16);
    }
    if (!id) {
      id = `remeha-config-note_${uuidv1()}`;
      n.id = id;
    }

    m[id] = info;
  }

  getNoteComponentInfoMap() {
    return this.state ? this.state.noteInfoMap : null;
  }

  newNoteComponentInfoMap() {
    return {};
  }

  isNoteOpened(note) {
    if (!note) {
      return false;
    }
    const id = typeof (note) === 'string' ? note : note.id;
    if (!id) {
      return false;
    }
    const map = this.getNoteComponentInfoMap();
    if (!map) {
      return false;
    }
    const info = map[id];

    return info ? info.opened : false;
  }

  setNoteOpened(note, opened, updateState = true) {
    let map = this.getNoteComponentInfoMap();
    if (!map) {
      map = this.newNoteComponentInfoMap();
    }
    const oldOpenedNote = this.state ? this.state.openedNote : null;

    if (oldOpenedNote && oldOpenedNote !== note) {
      this.setNoteComponentProperty(oldOpenedNote, 'opened', false, map);
    }

    this.setNoteComponentProperty(note, 'opened', opened, map);

    if (updateState) {
      let openedNote = null;
      if (note !== oldOpenedNote || opened) {
        openedNote = note;
      }
      this.setState({ openedNote, noteInfoMap: map });
    }
  }

  getOpenedNote() {
    return this.state.openedNote;
  }

  toggleNoteOpened(note) {
    if (!note) {
      return;
    }
    const opened = this.isNoteOpened(note);
    this.setNoteOpened(note, opened);
  }

  removeNoteHandler(note) {
    if (!note) {
      return;
    }
    const { onRemoveNote } = this.props;

    if (onRemoveNote) {
      if (onRemoveNote(note, this) === false) {
        return;
      }
    }

    let id;
    if (typeof (note) === 'string') {
      id = note;
    } else {
      id = note.id;
    }
    const map = this.getNoteComponentInfoMap();
    if (map) {
      delete map[id];
    }
    let openedNote = this.state.openedNote;
    if (openedNote === note) {
      openedNote = null;
    }

    const config = this.props.config;
    const notes = getConfigNotes(this.props.config);
    const num = notes ? notes.length : 0;
    let updated = false;
    for (let i = num - 1; i >= 0; i -= 1) {
      const item = notes[i];
      if (item === note || item.id === id) {
        updated = true;
        notes.splice(i, 1);
      }
    }
    if (updated) {
      this.setState({ openedNote, config });
      this.notifyRemovedNote(note);
    }
  }

  notifyRemovedNote(note) {
    const { onRemovedNote } = this.props;
    if (onRemovedNote) {
      onRemovedNote(note, this);
    }
  }

  notifyUpdateNotes() {
    const { onUpdateNotes } = this.props;
    if (onUpdateNotes) {
      onUpdateNotes();
    }
  }

  setNoteComponentProperty(note, property, value, map) {
    let info = this.getNoteComponentInfo(note, map);

    if (!info) {
      info = {};
      this.setNoteComponentInfo(note, info, map);
    }
    info[property] = value;
  }

  closeOpenedNote() {
    this.setNoteOpened(this.state.openedNote, false);
  }

  getTranslation(key, fallback) {
    const { onGetTranslation } = this.props;
    if (!onGetTranslation) {
      return onGetTranslation(key, fallback);
    }
    return fallback;
  }

  /* Render */

  renderInfoBubble(key, x, y, content, closed, clickNoteOption, clickClose, onUpdateContent) {
    return (
      <NoteInfoBubble
        key={key}
        closed={closed}
        editPlaceholder={this.getTranslation('configuration.comment.insertPlaceholder', 'Insert comment')}
        ref={(r) => { this.infoBubbleRef = r; }}
        x={x}
        y={y}
        initialEditing={true}
        onClickClose={clickClose}
        onClickOption={clickNoteOption}
        editable={this.props.notesEditable}
        removable={this.props.notesRemovable}
        content={content}
        onUpdateContent={onUpdateContent}
      />
    );
  }

  renderNote(note, index) {
    const { zoom } = this.props;

    const absX = (note.x * zoom);
    const absY = (note.y * zoom);

    let pressHandler = null;
    if (this.props.onPressNote) {
      pressHandler = this.notePressHandler;
    }

    let style = null;

    let { noteScale } = this.props;
    if (noteScale) {
      if (noteScale === 'auto') {
        noteScale = zoom;
      }
      if (typeof (noteScale) === 'number') {
        style = {
          transform: cssTransform(`scale(${noteScale}, ${noteScale})`),
        };
      }
    }
    if (!note.id) {
      const n = note;
      n.id = newConfigNoteID();
    }
    const key = note.id;

    return (
      <NoteObject
        key={key}

        x={absX}
        y={absY}
        note={note}
        noteIndex={index}

        style={style}

        onPress={pressHandler}
        onClick={this.noteClickHandler}
        onDragged={this.noteDraggedHandler}
      />
    );
  }

  render() {
    const { config, x, y, zoom } = this.props;
    const notes = getConfigNotes(config);

    if (!notes) {
      return null;
    }
    let openedNote = this.state.openedNote;

    let validOpenedNote = false;
    let noteElements = notes ? notes.map((note, index) => {
      if (note === openedNote) {
        validOpenedNote = true;
      }
      return this.renderNote(note, index);
    }) : null;

    if (!validOpenedNote) {
      openedNote = null;
      this.state.openedNote = openedNote;
    }

    if (openedNote) {
      const clickNoteOptionHandler = (option) => {
        this.noteClickOptionHandler(openedNote, option);
      };
      const clickNoteCloseHandler = () => {
        this.closeOpenedNote();
      };
      const infoX = (openedNote.x * zoom);
      const infoY = (openedNote.y * zoom);
      if (!noteElements) {
        noteElements = [];
      }
      const onUpdateInfoBubbleContent = this.updateNoteContentHandler;
      noteElements.push(this.renderInfoBubble(` $opened-note$ ${openedNote.id} `, infoX, infoY, openedNote.content, false, clickNoteOptionHandler, clickNoteCloseHandler, onUpdateInfoBubbleContent));
    }

    const noteContainerStyle = {
      position: 'absolute',
      left: `${Math.round(x)}px`,
      top: `${Math.round(y)}px`,
    };
    return (
      <div className="config-view__layer" style={noteContainerStyle}>
        {noteElements}
      </div>
    );
  }
}
NoteLayer.propTypes = {
  config: PropTypes.object,

  // Transform
  x: PropTypes.number,
  y: PropTypes.number,
  zoom: PropTypes.number,

  noteScale: PropTypes.number,
  notesEditable: PropTypes.bool,
  notesRemovable: PropTypes.bool,

  // Callbacks
  onPressNote: PropTypes.func,
  onClickNote: PropTypes.func,
  onClickNoteOption: PropTypes.func,
  onUpdateInfoBubbleContent: PropTypes.func,
  onUpdateNotes: PropTypes.func,
  onGetTranslation: PropTypes.func,
  onDraggedNote: PropTypes.func,
  onRemoveNote: PropTypes.func,
  onRemovedNote: PropTypes.func,
};



// WEBPACK FOOTER //
// ./js/components/configuration/note-layer.jsx